import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import withRouter from "components/Common/withRouter";
import { Card, CardBody, Container } from "reactstrap";
import { toast } from "react-toastify";

import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import CallDetailsBadFitResolutionModal from "pages/CallDetails/callDetailsBadFitResolutionModal";
import CandidateOnboardingTableContainer from "components/Common/CandidateOnboardingTableContainer";
import MetadataFilterToolbar from "components/Common/MetadataFilterToolbar";
import CandidateListFilterPrompt from "components/Common/CandidateListFilterPrompt";
import {
  DEFAULT_PAGE_SIZE,
  resolveMetadataTableColumnsFromApiResponse,
  pascalToCamel,
  urlEndPoint,
} from "helpers/GeneralHelper";
import {
  getAsyncFilterLabelFieldKey,
  isAsyncFilterLabelFieldKey,
  isMetadataAsyncSelectFilterDefinition,
  mergeModalSubmitIntoListFilter,
  normalizeMetadataFilterDateForApi,
} from "helpers/metadataFilterHelpers";
import { getBadFitMetadata, submitBadFitFilterData } from "model/BadFitModel";
import Loaders from "components/Common/Loaders";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";
import { usePermissionContext } from "libs/context/PermissionContext";
import { useCandidateTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";
import { useMetadataDrivenFilters } from "hooks/useMetadataDrivenFilters";

const BAD_FIT_FROM_AI_DASHBOARD_PARAM = "ai-dashboard";

/** Metadata PascalCase fields that differ from filter API camelCase keys. */
const BAD_FIT_METADATA_FIELD_TO_ACCESSOR = {
  aICandidateBadFitTrackingId: "aiCandidateBadFitTrackingId",
  aIDecisionAtUtc: "aiDecisionAtUtc",
};

const resolveBadFitColumnAccessor = (col = {}) => {
  const field = String(col?.field ?? "").trim();
  if (BAD_FIT_METADATA_FIELD_TO_ACCESSOR[field]) {
    return BAD_FIT_METADATA_FIELD_TO_ACCESSOR[field];
  }
  return field ? pascalToCamel(field) : null;
};

const extractBadFitFilterEnvelope = (response = {}) => {
  const responseData = response?.data ?? response ?? {};
  if (Array.isArray(responseData?.records)) {
    return responseData;
  }
  const nestedData =
    responseData?.data && typeof responseData.data === "object" && !Array.isArray(responseData.data)
      ? responseData.data
      : responseData;
  return nestedData && typeof nestedData === "object" ? nestedData : {};
};

const resolveBadFitCandidateClientId = (row = {}) =>
  String(
    row?.candidateClientId ??
      row?._originalBadFit?.candidateClientId ??
      ""
  ).trim();

const normalizeOptionalStringFilterValueOrNull = (rawValue) => {
  if (rawValue === null || rawValue === undefined) return null;
  const trimmed = String(rawValue).trim();
  return trimmed === "" ? null : trimmed;
};

const normalizeBadFitFilterPayload = (
  rawFilterBody = {},
  metadataFiltersFromApi = []
) => {
  const outboundPayload = { ...rawFilterBody };

  const dynamicNormalizeKeysFromMetadata = (
    Array.isArray(metadataFiltersFromApi) ? metadataFiltersFromApi : []
  ).reduce((uniqueKeyAccumulator, filterDefinitionRow) => {
    const backendFieldIdentifier = filterDefinitionRow?.field;
    if (
      backendFieldIdentifier &&
      backendFieldIdentifier !== "search" &&
      !uniqueKeyAccumulator.includes(backendFieldIdentifier)
    ) {
      return [...uniqueKeyAccumulator, backendFieldIdentifier];
    }
    return uniqueKeyAccumulator;
  }, []);

  const normalizedStringKeys = Array.from(
    new Set(["search", ...dynamicNormalizeKeysFromMetadata])
  );

  normalizedStringKeys.forEach((keywordKey) => {
    if (keywordKey in outboundPayload) {
      outboundPayload[keywordKey] = normalizeOptionalStringFilterValueOrNull(
        outboundPayload[keywordKey]
      );
    }
  });

  const isResolvedFilterValue = outboundPayload.isResolved;
  if (isResolvedFilterValue === true || isResolvedFilterValue === false) {
    outboundPayload.isResolved = isResolvedFilterValue;
  } else if (isResolvedFilterValue === "true" || isResolvedFilterValue === "yes") {
    outboundPayload.isResolved = true;
  } else if (isResolvedFilterValue === "false" || isResolvedFilterValue === "no") {
    outboundPayload.isResolved = false;
  } else if ("isResolved" in outboundPayload) {
    outboundPayload.isResolved = null;
  }

  if (!outboundPayload.fromDate && outboundPayload.fromDate !== 0) {
    outboundPayload.fromDate = null;
  } else if (outboundPayload.fromDate === "") {
    outboundPayload.fromDate = null;
  }
  if (!outboundPayload.toDate && outboundPayload.toDate !== 0) {
    outboundPayload.toDate = null;
  } else if (outboundPayload.toDate === "") {
    outboundPayload.toDate = null;
  }

  Object.keys(outboundPayload).forEach((payloadKey) => {
    if (isAsyncFilterLabelFieldKey(payloadKey)) {
      delete outboundPayload[payloadKey];
    }
  });

  return outboundPayload;
};

const BadFit = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromAiDashboard = searchParams.get("from") === BAD_FIT_FROM_AI_DASHBOARD_PARAM;
  const aiDashboardAutoFilterHandledRef = useRef(false);

  const { permissionData } = usePermissionContext() || {};
  const { fetchMetadataSchema } = useCommonFilterOptions();
  const { pageSize: settingsPageSize, settingsReady } =
    useCandidateTableSettingsPageSize("BadFit");
  const [badFitFilterApplied, setBadFitFilterApplied] = useState(false);
  const [toolbarFilterRemountKey, setToolbarFilterRemountKey] = useState(0);
  const [badFitListFilter, setBadFitListFilter] = useState({});
  const [badFitData, setBadFitData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
  const [commonResetFilter, setCommonResetFilter] = useState(false);
  const [requestPayload, setRequestPayload] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    search: null,
  });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });
  const [tableMetaData, setTableMetaData] = useState([]);
  const [badFitFilterMetaData, setBadFitFilterMetaData] = useState([]);
  const [badFitResolutionModalOpen, setBadFitResolutionModalOpen] = useState(false);
  const [badFitResolutionRow, setBadFitResolutionRow] = useState(null);

  const {
    metadataFiltersAll,
    metadataToolbarFilterDefinitions: badFitMetadataToolbarFilterDefinitions,
    metadataModalOnlyFilterDefinitions: badFitMetadataModalOnlyFilterDefinitions,
    usesMetadataDrivenFilterUi: usesMetadataDrivenBadFitFilterUi,
    shouldShowTableHeaderFilterAndResetControls,
    shouldRenderMetadataToolbarFilterRow: shouldRenderBadFitMetadataToolbarFilterRow,
    modalFilterData: metadataDrivenModalFilterData,
    activeModalFilterCount,
    buildBaselineEmptyListFilter,
    toolbarDropdownReactSelectOptionsByFieldName,
  } = useMetadataDrivenFilters(badFitFilterMetaData, badFitListFilter, "BadFit");

  const buildBaselineEmptyBadFitFilterState = useCallback(() => {
    return buildBaselineEmptyListFilter();
  }, [buildBaselineEmptyListFilter]);

  const badFitFilterModalData = useMemo(() => {
    if (badFitMetadataModalOnlyFilterDefinitions.length) {
      return metadataDrivenModalFilterData;
    }
    if (usesMetadataDrivenBadFitFilterUi) {
      return [];
    }
    return [];
  }, [
    badFitMetadataModalOnlyFilterDefinitions,
    metadataDrivenModalFilterData,
    usesMetadataDrivenBadFitFilterUi,
  ]);

  useEffect(() => {
    if (!Array.isArray(badFitFilterMetaData) || !badFitFilterMetaData.length) return;
    setBadFitListFilter((previousFilterState) => {
      const mergedFilterState = { ...previousFilterState };
      let mergedNewKeysIntoState = false;
      badFitFilterMetaData.forEach((filterDefinitionFromMetadataApi) => {
        const backendFilterFieldIdentifier = filterDefinitionFromMetadataApi?.field;
        if (!backendFilterFieldIdentifier) return;
        if (!(backendFilterFieldIdentifier in mergedFilterState)) {
          mergedFilterState[backendFilterFieldIdentifier] =
            filterDefinitionFromMetadataApi?.type === "date" ? null : "";
          mergedNewKeysIntoState = true;
        }
        if (isMetadataAsyncSelectFilterDefinition(filterDefinitionFromMetadataApi)) {
          const asyncLabelFieldKey = getAsyncFilterLabelFieldKey(
            backendFilterFieldIdentifier
          );
          if (!(asyncLabelFieldKey in mergedFilterState)) {
            mergedFilterState[asyncLabelFieldKey] = "";
            mergedNewKeysIntoState = true;
          }
        }
      });
      return mergedNewKeysIntoState ? mergedFilterState : previousFilterState;
    });
  }, [badFitFilterMetaData]);

  const mapRecordsToRows = (list = []) =>
    list.map((item = {}) => ({
      ...item,
      _originalBadFit: { ...item },
      id:
        item?.aiCandidateBadFitTrackingId ||
        item?.aICandidateBadFitTrackingId ||
        item?.candidateClientId ||
        "",
    }));

  const buildPayloadFromFilterForm = (form = {}, overrides = {}) => {
    const searchKeywordTrimmed = String(form?.search || "").trim();
    const fromDateForFilter = normalizeMetadataFilterDateForApi(form?.fromDate);
    const toDateForFilter = normalizeMetadataFilterDateForApi(form?.toDate);

    const looselyMappedMetadataFilterValuesFromUiFormLayer = {};
    if (Array.isArray(badFitFilterMetaData)) {
      badFitFilterMetaData.forEach((filterDefinitionFromMetadataApi) => {
        const backendFilterFieldIdentifier = filterDefinitionFromMetadataApi?.field;
        if (!backendFilterFieldIdentifier || backendFilterFieldIdentifier === "search") {
          return;
        }
        looselyMappedMetadataFilterValuesFromUiFormLayer[backendFilterFieldIdentifier] =
          form?.[backendFilterFieldIdentifier];
      });
    }

    return normalizeBadFitFilterPayload(
      {
        ...looselyMappedMetadataFilterValuesFromUiFormLayer,
        pageNumber: 1,
        pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
        search: normalizeOptionalStringFilterValueOrNull(searchKeywordTrimmed),
        fromDate: fromDateForFilter,
        toDate: toDateForFilter,
        ...overrides,
      },
      badFitFilterMetaData
    );
  };

  const getBadFitList = async (payloadOverrides = {}, options = {}) => {
    const { onlyPagination = false } = options;
    setLoading(true);
    try {
      const payload = onlyPagination
        ? {
            pageNumber: Number(payloadOverrides.pageNumber) || 1,
            pageSize: Number(payloadOverrides.pageSize) || DEFAULT_PAGE_SIZE,
          }
        : normalizeBadFitFilterPayload(
            {
              ...requestPayload,
              ...payloadOverrides,
            },
            badFitFilterMetaData
          );

      const response = await submitBadFitFilterData(payload);
      const filterEnvelope = extractBadFitFilterEnvelope(response);
      const records = Array.isArray(filterEnvelope?.records) ? filterEnvelope.records : [];
      setBadFitData(mapRecordsToRows(records));

      if (response == null || response?.success !== false) {
        setPaginationMeta({
          pageNumber: Number(filterEnvelope?.pageNumber) || Number(payload?.pageNumber) || 1,
          pageSize: Number(filterEnvelope?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
          totalPages: Number(filterEnvelope?.totalPages) || 0,
          totalRecords: Number(filterEnvelope?.totalRecords) || records.length,
        });
        if (onlyPagination) {
          const clearedServerFilterSnapshotAfterPaginationReset =
            normalizeBadFitFilterPayload(
              {
                pageNumber: payload.pageNumber,
                pageSize: payload.pageSize,
                search: null,
                fromDate: null,
                toDate: null,
                isResolved: null,
                ...Object.fromEntries(
                  (Array.isArray(badFitFilterMetaData) ? badFitFilterMetaData : [])
                    .filter(
                      (filterDefinitionFromMetadataApi) =>
                        filterDefinitionFromMetadataApi?.field &&
                        filterDefinitionFromMetadataApi.field !== "search"
                    )
                    .map((filterDefinitionFromMetadataApi) => [
                      filterDefinitionFromMetadataApi.field,
                      null,
                    ])
                ),
              },
              badFitFilterMetaData
            );
          setRequestPayload(clearedServerFilterSnapshotAfterPaginationReset);
          setBadFitListFilter(buildBaselineEmptyBadFitFilterState());
        } else {
          setRequestPayload(normalizeBadFitFilterPayload(payload, badFitFilterMetaData));
        }
      } else {
        setBadFitData([]);
        if (response) {
          toast.error(response?.message || "Unable to load bad fit records");
        }
      }
    } catch (err) {
      toast.error(err?.message || "Unable to load bad fit records");
      setBadFitData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);
      try {
        const metaRes = await fetchMetadataSchema("metadata:badFit", () => getBadFitMetadata());
        const metadataFilters = Array.isArray(metaRes?.data?.filters)
          ? metaRes.data.filters
          : Array.isArray(metaRes?.filters)
            ? metaRes.filters
            : [];

        const tableColumns = resolveMetadataTableColumnsFromApiResponse(metaRes, {
          resolveAccessor: resolveBadFitColumnAccessor,
        });
        setTableMetaData(tableColumns);
        setBadFitFilterMetaData(metadataFilters);
      } catch (e) {
        toast.error(e?.message || "Unable to load bad fit metadata");
        setTableMetaData([]);
        setBadFitFilterMetaData([]);
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, []);

  useEffect(() => {
    if (!settingsReady) return;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) =>
      previous.pageSize === pageSize ? previous : { ...previous, pageSize }
    );
    setPaginationMeta((previous) =>
      previous.pageSize === pageSize ? previous : { ...previous, pageSize }
    );
  }, [settingsReady, settingsPageSize]);

  useEffect(() => {
    if (!fromAiDashboard || aiDashboardAutoFilterHandledRef.current) return;
    if (!settingsReady) return;
    if (!Array.isArray(badFitFilterMetaData) || !badFitFilterMetaData.length) return;

    aiDashboardAutoFilterHandledRef.current = true;
    setBadFitFilterApplied(true);
    const defaultFilter = buildBaselineEmptyBadFitFilterState();
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;

    setBadFitListFilter(defaultFilter);
    setToolbarFilterRemountKey((previousKey) => previousKey + 1);
    void getBadFitList(buildPayloadFromFilterForm(defaultFilter, { pageSize }));
    navigate(location.pathname, { replace: true });
  }, [
    fromAiDashboard,
    settingsReady,
    settingsPageSize,
    badFitFilterMetaData,
    buildBaselineEmptyBadFitFilterState,
    navigate,
    location.pathname,
  ]);

  const handleBadFitFilterSubmit = async (filterFormValuesFromModal = {}) => {
    const mergedBadFitListFilterAfterModalSave = mergeModalSubmitIntoListFilter(
      badFitListFilter,
      badFitFilterModalData,
      filterFormValuesFromModal
    );
    setBadFitListFilter(mergedBadFitListFilterAfterModalSave);
    setCommonFilterModalOpen(false);
    setBadFitFilterApplied(true);
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    const nextServerFilterPayload = buildPayloadFromFilterForm(
      mergedBadFitListFilterAfterModalSave,
      { pageSize }
    );
    await getBadFitList(nextServerFilterPayload);
  };

  const handleBadFitFilterReset = async () => {
    setBadFitFilterApplied(false);
    const resetBadFitFilterStateMatchingMetadataKeys = buildBaselineEmptyBadFitFilterState();
    setBadFitListFilter(resetBadFitFilterStateMatchingMetadataKeys);
    setToolbarFilterRemountKey((previousKey) => previousKey + 1);
    setCommonFilterModalOpen(false);
    setBadFitData([]);
    setPaginationMeta({
      pageNumber: 1,
      pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
      totalPages: 0,
      totalRecords: 0,
    });
    setRequestPayload(
      normalizeBadFitFilterPayload(
        {
          pageNumber: 1,
          pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
          search: null,
          fromDate: null,
          toDate: null,
          isResolved: null,
          ...Object.fromEntries(
            (Array.isArray(badFitFilterMetaData) ? badFitFilterMetaData : [])
              .filter(
                (filterDefinitionFromMetadataApi) =>
                  filterDefinitionFromMetadataApi?.field &&
                  filterDefinitionFromMetadataApi.field !== "search"
              )
              .map((filterDefinitionFromMetadataApi) => [
                filterDefinitionFromMetadataApi.field,
                null,
              ])
          ),
        },
        badFitFilterMetaData
      )
    );
  };

  const handleUpperToolbarBadFitFilterSearch = async () => {
    setBadFitFilterApplied(true);
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    const nextServerFilterPayloadFromToolbar = buildPayloadFromFilterForm(badFitListFilter, {
      pageSize,
    });
    await getBadFitList(nextServerFilterPayloadFromToolbar);
  };

  const handleToolbarTextFilterInputChange = (backendFilterFieldKey, nextInputStringValue) => {
    setBadFitListFilter((previousFilterState) => ({
      ...previousFilterState,
      [backendFilterFieldKey]: nextInputStringValue ?? "",
    }));
  };

  const handleToolbarReactSelectFilterChange = (backendFilterFieldKey, nextSelectedOption) => {
    setBadFitListFilter((previousFilterState) => ({
      ...previousFilterState,
      [backendFilterFieldKey]: nextSelectedOption?.value ?? "",
      [getAsyncFilterLabelFieldKey(backendFilterFieldKey)]:
        nextSelectedOption?.label ?? "",
    }));
  };

  useEffect(() => {
    if (!commonResetFilter) return;
    const run = async () => {
      await handleBadFitFilterReset();
      setCommonResetFilter(false);
    };
    run();
  }, [commonResetFilter]);

  const handlePageChange = async (page) => {
    if (!badFitFilterApplied) return;
    await getBadFitList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    if (!badFitFilterApplied) return;
    await getBadFitList({ pageNumber: 1, pageSize: rows });
  };

  const filteredData = useMemo(() => badFitData, [badFitData]);

  const badFitActionPermissions = useMemo(
    () => ({
      canCallDetails: Boolean(
        permissionData?.CallCallDetailsAccess || permissionData?.CallDetailsAccess
      ),
      canConvertToGoodFit: true,
    }),
    [permissionData]
  );

  const hasAnyBadFitRowAction = useMemo(
    () =>
      badFitActionPermissions.canCallDetails ||
      badFitActionPermissions.canConvertToGoodFit,
    [badFitActionPermissions]
  );

  const handleBadFitConvertToGoodFit = (row = {}) => {
    const rawRow = row?._originalBadFit ?? row ?? {};
    setBadFitResolutionRow(rawRow);
    setBadFitResolutionModalOpen(true);
  };

  const closeBadFitResolutionModal = () => {
    setBadFitResolutionModalOpen(false);
    setBadFitResolutionRow(null);
  };

  const handleBadFitResolutionSuccess = async () => {
    closeBadFitResolutionModal();
    if (!badFitFilterApplied) return;
    await getBadFitList({ pageNumber: paginationMeta?.pageNumber || 1 });
  };

  const badFitResolutionCandidateClientId = String(
    badFitResolutionRow?.candidateClientId ?? ""
  ).trim();
  const badFitResolutionTrackingId =
    badFitResolutionRow?.aiCandidateBadFitTrackingId ??
    badFitResolutionRow?.aICandidateBadFitTrackingId ??
    null;

  return (
    <div className="page-content bad-fit-page">
      <Container fluid>
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h5 className="fw-bold mb-0">Bad Fit</h5>
            </div>
            <hr />

            {shouldRenderBadFitMetadataToolbarFilterRow ? (
              <MetadataFilterToolbar
                remountKey={toolbarFilterRemountKey}
                toolbarDefinitions={badFitMetadataToolbarFilterDefinitions}
                listFilter={badFitListFilter}
                onTextChange={handleToolbarTextFilterInputChange}
                onSelectChange={handleToolbarReactSelectFilterChange}
                onSearch={handleUpperToolbarBadFitFilterSearch}
                onClear={handleBadFitFilterReset}
                showClearWhenNoModalFilters={!shouldShowTableHeaderFilterAndResetControls}
                toolbarDropdownOptionsByFieldName={toolbarDropdownReactSelectOptionsByFieldName}
                layoutPreset="candidates"
                pageName="BadFitResolution"
              />
            ) : null}

            {loading ? (
              <Loaders />
            ) : !tableMetaData?.length ? (
              <div className="text-muted text-center py-5">Unable to load table columns.</div>
            ) : (
              <CandidateOnboardingTableContainer
                columns={tableMetaData}
                data={badFitFilterApplied ? filteredData : []}
                emptyTableContent={
                  !badFitFilterApplied ? (
                    <CandidateListFilterPrompt
                      variant="table"
                      title="Apply filters to view bad fit records"
                      description="Choose your filter criteria above and click Search. Your results will appear in this table."
                      onSearch={handleUpperToolbarBadFitFilterSearch}
                    />
                  ) : undefined
                }
                isGlobalFilter={false}
                isSort={true}
                isCheckboxLeftSide={false}
                isActionRequired={hasAnyBadFitRowAction}
                getDetailsHref={
                  badFitActionPermissions.canCallDetails
                    ? (row) => {
                        const id = resolveBadFitCandidateClientId(row);
                        return id ? `/${urlEndPoint.callDetails}/${id}` : "";
                      }
                    : undefined
                }
                onBadFitConvertToGoodFit={
                  badFitActionPermissions.canConvertToGoodFit
                    ? handleBadFitConvertToGoodFit
                    : undefined
                }
                tableClass="align-middle table-nowrap mb-0"
                theadClass="table-light"
                pageName="BadFitResolution"
                isPagination={true}
                currentPage={paginationMeta?.pageNumber}
                rowsPerPage={paginationMeta?.pageSize}
                totalPages={paginationMeta?.totalPages}
                totalRecords={paginationMeta?.totalRecords}
                onPageChange={handlePageChange}
                onPageSizeChange={handleRowsPerPageChange}
                setCommonFilterModalOpen={setCommonFilterModalOpen}
                setCommonResetFilter={setCommonResetFilter}
                allCommonFilter={shouldShowTableHeaderFilterAndResetControls}
                activeFilterCount={activeModalFilterCount}
              />
            )}
          </CardBody>
        </Card>

        <AllCommonFilterModal
          setCommonFilterModalOpen={setCommonFilterModalOpen}
          commonFilterModalOpen={commonFilterModalOpen}
          modalTitle="Bad Fit Filter"
          handleSubmit={handleBadFitFilterSubmit}
          filterData={badFitFilterModalData}
          metadataFilterDefinitions={metadataFiltersAll}
          enableFilterToolbarCustomization={usesMetadataDrivenBadFitFilterUi}
          commonResetFilter={commonResetFilter}
          setCommonResetFilter={setCommonResetFilter}
          pageName="BadFitResolution"
        />

        <CallDetailsBadFitResolutionModal
          isOpen={badFitResolutionModalOpen}
          onClose={closeBadFitResolutionModal}
          candidateClientId={badFitResolutionCandidateClientId}
          badFitTrackingId={badFitResolutionTrackingId}
          onSuccess={handleBadFitResolutionSuccess}
        />
      </Container>
    </div>
  );
};

BadFit.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(BadFit);
